import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || 'pandabuy-spreadsheet-guide');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-[1200px] mx-auto px-4 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
            pandabuy<span className="text-primary-500">.skin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/article/pandabuy-spreadsheet-guide" className="hidden sm:inline-flex text-xs font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
              Shopping Guide
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <article className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <aside className="lg:w-[30%] flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <span className="inline-block px-4 py-1.5 rounded-full border border-background-300/60 text-xs font-medium text-foreground-600 tracking-wide bg-background-50">
                  {article.tag}
                </span>
                <div className="mt-6 rounded-3xl overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-[380px] md:h-[460px] object-cover object-top"
                  />
                </div>

                <nav className="mt-8 p-5 rounded-2xl bg-background-100 border border-background-200/60">
                  <h3 className="font-semibold text-foreground-950 text-sm mb-3">Quick Links</h3>
                  <ul className="space-y-2">
                    {article.sections.map((s) => (
                      <li key={s.id}>
                        <a href={`#${s.id}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">{s.heading}</a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="mt-6 p-5 rounded-2xl bg-primary-50 border border-primary-200/50">
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    All external links on <Link to="/" className="text-primary-500 font-medium hover:underline">pandabuy.skin</Link> direct to verified Pandabuy Spreadsheet collections on kaspreadsheet.com.
                  </p>
                </div>
              </div>
            </aside>

            <div className="lg:w-[70%]">
              <header className="mb-12">
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.1]">
                  {article.title}
                </h1>
                <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed max-w-[640px]">
                  {article.subtitle}
                </p>
              </header>

              {article.sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-12">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pi) => (
                    <p key={pi} className="text-foreground-600 leading-relaxed mb-4 text-sm md:text-base">
                      {p}
                    </p>
                  ))}
                </section>
              ))}

              <div className="mt-14 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/60">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                  Ready to Start Shopping Smarter?
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                  Browse our full PandabuySpreadsheet database with 14 curated categories and thousands of verified product listings.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
                  >
                    <i className="ri-home-4-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 font-semibold text-sm hover:bg-foreground-800 transition-all whitespace-nowrap"
                  >
                    Pandabuy Sheet
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.subtitle,
          "image": article.imageUrl,
          "url": `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`,
          "datePublished": "2026-06-29T00:00:00+00:00",
          "dateModified": "2026-06-29T00:00:00+00:00",
          "author": {
            "@type": "Organization",
            "name": "pandabuy.skin",
            "url": import.meta.env.VITE_SITE_URL
          },
          "publisher": {
            "@type": "Organization",
            "name": "pandabuy.skin",
            "url": import.meta.env.VITE_SITE_URL,
            "logo": {
              "@type": "ImageObject",
              "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa4b8144b8a.jpeg"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`
          }
        }),
      }} />

      <footer className="bg-foreground-900 text-background-50 py-10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-10 text-center">
          <Link to="/" className="font-heading text-lg font-bold text-white">
            pandabuy<span className="text-primary-400">.skin</span>
          </Link>
          <p className="mt-3 text-sm text-foreground-400">
            &copy; 2026 pandabuy.skin — All rights reserved.
          </p>
          <p className="mt-4 text-xs text-foreground-500 flex flex-wrap justify-center gap-x-4 gap-y-1">
            <Link to="/" className="hover:text-primary-400 transition-colors">Home</Link>
            <Link to="/article/pandabuy-spreadsheet-guide" className="hover:text-primary-400 transition-colors">Shopping Guide</Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-primary-400 transition-colors">Pandabuy Sheet</a>
          </p>
        </div>
      </footer>
    </main>
  );
}